// Lead field names are typed by whoever builds the lead form, so "full name", "Full_Name",
// "E-Mail" or "Telefon nummer" all show up in real data. Keep the alias list in sync with
// server/src/util/meta/leadGenForms/leadFieldData.ts.

export type LeadFieldType = 'full_name' | 'first_name' | 'last_name' | 'email' | 'phone_number'

export const normalizeFieldName = (name: unknown): string => {
  if (typeof name !== 'string') return ''
  return name
    .trim()
    .toLowerCase()
    .replace(/[?!:.*]+$/g, '')
    .replace(/[\s\-./]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '')
}

const ALIASES: Record<LeadFieldType, string[]> = {
  full_name: [
    'full_name',
    'fullname',
    'name',
    'your_name',
    'ihr_name',
    'dein_name',
    'vorname_nachname',
    'vorname_und_nachname',
    'vor_und_nachname',
    'vollstandiger_name',
    'vollständiger_name',
    'voller_name',
    'kontaktname',
    'applicant_name',
    'bewerber',
  ],
  first_name: ['first_name', 'firstname', 'given_name', 'vorname', 'vname'],
  last_name: ['last_name', 'lastname', 'surname', 'family_name', 'nachname', 'familienname'],
  email: [
    'email',
    'e_mail',
    'mail',
    'email_address',
    'e_mail_address',
    'emailadresse',
    'e_mail_adresse',
    'email_adresse',
    'work_email',
    'business_email',
  ],
  phone_number: [
    'phone_number',
    'phonenumber',
    'phone',
    'telephone',
    'tel',
    'mobile',
    'mobile_number',
    'cell',
    'handy',
    'handynummer',
    'mobilnummer',
    'telefon',
    'telefonnummer',
    'telefon_nummer',
    'rufnummer',
    'work_phone_number',
  ],
}

const ALIAS_TO_TYPE = new Map<string, LeadFieldType>()
;(Object.keys(ALIASES) as LeadFieldType[]).forEach((type) =>
  ALIASES[type].forEach((alias) => ALIAS_TO_TYPE.set(alias, type))
)

// Long keys are questions that merely mention a contact word, so the substring match is capped.
const HEURISTIC_MAX_LENGTH = 30

/** Which standard applicant field a form field is, or null when it is a form question. */
export const classifyLeadField = (name: unknown): LeadFieldType | null => {
  const key = normalizeFieldName(name)
  if (!key) return null
  const exact = ALIAS_TO_TYPE.get(key)
  if (exact) return exact
  if (key.length > HEURISTIC_MAX_LENGTH) return null
  if (key.includes('mail')) return 'email'
  if (/(phone|telefon|handy|mobil)/.test(key)) return 'phone_number'
  return null
}

/** First value of a field, tolerating `values` being missing, a plain string, or non-string. */
export const firstFieldValue = (field: { values?: unknown } | null | undefined): string => {
  const raw = field?.values
  const first = Array.isArray(raw) ? raw[0] : raw
  return first === null || first === undefined || typeof first === 'object'
    ? ''
    : String(first).trim()
}

/** Parse fieldData that is a JSON string or already an array; never throws. */
export const parseFieldData = (fieldData: unknown): Array<{ name: string; values?: unknown }> => {
  let data = fieldData
  if (typeof data === 'string') {
    try {
      data = JSON.parse(data)
    } catch {
      return []
    }
  }
  return Array.isArray(data)
    ? data.filter((f) => f && typeof f === 'object' && typeof f.name === 'string')
    : []
}
